import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import { PersonOutlineOutlined } from '@mui/icons-material';
import EmployeeCard from './EmployeeCard';
import AddEmployeeCard from './AddEmployeeCard';
import { Employee } from '../../types/employee.types';

interface EmployeeListProps {
  employees: Employee[];
  isLoading: boolean;
  canWrite: boolean;
  onEdit: (employee: Employee) => void;
  onDelete: (employee: Employee) => void;
  onMarkInactive: (employee: Employee) => void;
  onAddEmployee: () => void;
  onInvite?: (employee: Employee) => void;
  onResendInvite?: (employee: Employee) => void;
  onPromote?: (employee: Employee) => void;
  onDemote?: (employee: Employee) => void;
  promotedEmployeeIds?: string[]; // IDs of employees who have been promoted
}

export default function EmployeeList({
  employees,
  isLoading,
  canWrite,
  onEdit,
  onDelete,
  onMarkInactive,
  onAddEmployee,
  onInvite,
  onResendInvite,
  onPromote,
  onDemote,
  promotedEmployeeIds = [],
}: EmployeeListProps) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (employees.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          textAlign: 'center',
        }}
      >
        <PersonOutlineOutlined sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No employees yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add your first employee to get started with scheduling
        </Typography>
        {canWrite && (
          <AddEmployeeCard onClick={onAddEmployee} disabled={isLoading} />
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {employees.map((employee) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={employee.id}>
          <EmployeeCard
            employee={employee}
            onEdit={onEdit}
            onDelete={onDelete}
            onMarkInactive={onMarkInactive}
            onInvite={onInvite}
            onResendInvite={onResendInvite}
            onPromote={onPromote}
            onDemote={onDemote}
            isPromoted={promotedEmployeeIds.includes(employee.id)}
          />
        </Grid>
      ))}
      {canWrite && (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <AddEmployeeCard onClick={onAddEmployee} disabled={isLoading} />
        </Grid>
      )}
    </Grid>
  );
}
